import React from 'react';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { SEOMeta } from 'afivio/templates/SEOMeta';
import { Button } from '@/components/Button';
import {
       CheckCircleIcon,
       UsersIcon,
       ChartBarIcon,
       CurrencyDollarIcon,
       ArrowRightIcon,
       ShieldCheckIcon,
       LockClosedIcon,
       ClipboardDocumentListIcon,
       QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { SeoInternalLinks } from '@/components/SeoInternalLinks';

interface AlternativePageTemplateProps {
    seoMeta?: { title: string; description: string; keywords: string; url: string };
       competitorName: string;
}

function cx(...classes: Array<string | false | undefined | null>) {
       return classes.filter(Boolean).join(" ");
}

function Pill({
                     children,
                     tone = "info",
              }: {
       children: React.ReactNode;
       tone?: "neutral" | "good" | "info";
}) {
       return (
           <span
               className={cx(
                   "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold",
                   tone === "neutral" && "border-slate-200 bg-white text-slate-700",
                   tone === "good" && "border-emerald-200 bg-emerald-50 text-emerald-800",
                   tone === "info" && "border-indigo-200 bg-indigo-50 text-indigo-800"
               )}
           >
                     {children}
              </span>
       );
}

function SectionCard({
                            title,
                            icon,
                            children,
                            subtitle,
                     }: {
       title: string;
       subtitle?: string;
       icon: React.ReactNode;
       children: React.ReactNode;
}) {
       return (
           <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3">
                         <div className="rounded-2xl border border-slate-200 bg-slate-50 p-2">{icon}</div>
                         <div className="min-w-0">
                                <h2 className="text-base font-extrabold text-slate-900">{title}</h2>
                                {subtitle ? <p className="mt-1 text-sm text-slate-600">{subtitle}</p> : null}
                         </div>
                  </div>
                  <div className="mt-5">{children}</div>
           </section>
       );
}

function FaqItem({ q, a }: { q: string; a: React.ReactNode }) {
       return (
           <details className="group rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold text-slate-900">
                         <span>{q}</span>
                         <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 group-open:hidden">
                                   +
                            </span>
                         <span className="hidden rounded-full border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 group-open:inline">
                                   –
                            </span>
                  </summary>
                  <div className="mt-3 text-sm text-slate-600 leading-relaxed">{a}</div>
           </details>
       );
}

export const AlternativePageTemplate: React.FC<AlternativePageTemplateProps> = ({ competitorName, seoMeta }) => {
       const router = useRouter();
       const t = useTranslations('alternatives');

       return (
           <>
                  <SEOMeta
                      title={seoMeta?.title || t('seo.title', { competitor: competitorName })}
                      description={seoMeta?.description || t('seo.description', { competitor: competitorName })}
                      keywords={seoMeta?.keywords || t('seo.keywords', { competitor: competitorName })}
                      url={seoMeta?.url || `/${competitorName.toLowerCase().replace(/\s+/g, '-')}-alternative`}
                  />

                  <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-white font-sans">
                         <div className="mx-auto max-w-6xl px-6 py-16">
                                {/* Header Section */}
                                <div className="relative mb-12 overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-indigo-50 via-white to-slate-50 p-8 shadow-sm lg:p-10">
                                       <div
                                           aria-hidden
                                           className="pointer-events-none absolute -right-16 -top-20 h-72 w-72 rounded-full bg-indigo-200/30 blur-3xl"
                                       />
                                       <div
                                           aria-hidden
                                           className="pointer-events-none absolute -bottom-20 -left-16 h-72 w-72 rounded-full bg-emerald-200/30 blur-3xl"
                                       />

                                       <div className="relative grid gap-8">
                                              <div className="max-w-3xl">
                                                     <div className="mb-6 flex flex-wrap items-center gap-2">
                                                            <Pill tone="info">
                                                                   <ShieldCheckIcon className="h-4 w-4" />
                                                                   {t('hero.badge')}
                                                            </Pill>
                                                            <Pill tone="neutral">
                                                                   <LockClosedIcon className="h-4 w-4" />
                                                                   {t('hero.gdpr')}
                                                            </Pill>
                                                            <Pill tone="good">
                                                                   <CheckCircleIcon className="h-4 w-4" />
                                                                   {t('hero.noCreditCard')}
                                                            </Pill>
                                                     </div>

                                                     <motion.h1
                                                         initial={{ opacity: 0, y: 8 }}
                                                         animate={{ opacity: 1, y: 0 }}
                                                         transition={{ duration: 0.35 }}
                                                         className="mb-6 text-4xl font-extrabold tracking-tight text-slate-900 lg:text-5xl"
                                                     >
                                                            {t('hero.title', { competitor: competitorName })}
                                                     </motion.h1>

                                                     <motion.p
                                                         initial={{ opacity: 0, y: 8 }}
                                                         animate={{ opacity: 1, y: 0 }}
                                                         transition={{ duration: 0.35, delay: 0.05 }}
                                                         className="mb-4 text-lg leading-relaxed text-slate-700"
                                                     >
                                                            {t('hero.p1')}
                                                     </motion.p>

                                                     <motion.p
                                                         initial={{ opacity: 0, y: 8 }}
                                                         animate={{ opacity: 1, y: 0 }}
                                                         transition={{ duration: 0.35, delay: 0.1 }}
                                                         className="text-lg leading-relaxed text-slate-700"
                                                     >
                                                            {t('hero.p2')}
                                                     </motion.p>

                                                     {/* CTA moved here (under text) */}
                                                     <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
                                                            <Button
                                                                blue
                                                                onClick={() => router.push('/signup')}
                                                                className="w-full py-4 text-base font-extrabold shadow-sm sm:w-auto sm:px-8"
                                                            >
          <span className="inline-flex items-center justify-center gap-2">
            {t('cta.button')}
                 <ArrowRightIcon className="h-5 w-5" />
          </span>
                                                            </Button>
                                                     </div>
                                              </div>
                                       </div>
                                </div>
                                {/* Features Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
                                       <SectionCard
                                           title={t('features.onboarding.title')}
                                           icon={<UsersIcon className="h-5 w-5 text-slate-700" />}
                                       >
                                              <p className="text-sm text-slate-600 leading-relaxed">
                                                     {t('features.onboarding.description')}
                                              </p>
                                              <ul className="mt-4 space-y-2">
                                                     <li className="flex items-center gap-2 text-sm text-slate-700">
                                                            <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                                                            {t('features.onboarding.item1')}
                                                     </li>
                                                     <li className="flex items-center gap-2 text-sm text-slate-700">
                                                            <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                                                            {t('features.onboarding.item2')}
                                                     </li>
                                              </ul>
                                       </SectionCard>

                                       <SectionCard
                                           title={t('features.payouts.title')}
                                           icon={<CurrencyDollarIcon className="h-5 w-5 text-slate-700" />}
                                       >
                                              <p className="text-sm text-slate-600 leading-relaxed">
                                                     {t('features.payouts.description')}
                                              </p>
                                              <ul className="mt-4 space-y-2">
                                                     <li className="flex items-center gap-2 text-sm text-slate-700">
                                                            <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                                                            {t('features.payouts.item1')}
                                                     </li>
                                                     <li className="flex items-center gap-2 text-sm text-slate-700">
                                                            <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                                                            {t('features.payouts.item2')}
                                                     </li>
                                              </ul>
                                       </SectionCard>

                                       <SectionCard
                                           title={t('features.tracking.title')}
                                           icon={<ChartBarIcon className="h-5 w-5 text-slate-700" />}
                                       >
                                              <p className="text-sm text-slate-600 leading-relaxed">
                                                     {t('features.tracking.description')}
                                              </p>
                                              <ul className="mt-4 space-y-2">
                                                     <li className="flex items-center gap-2 text-sm text-slate-700">
                                                            <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                                                            {t('features.tracking.item1')}
                                                     </li>
                                                     <li className="flex items-center gap-2 text-sm text-slate-700">
                                                            <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                                                            {t('features.tracking.item2')}
                                                     </li>
                                              </ul>
                                       </SectionCard>
                                </div>

                                {/* Workflow Section */}
                                <div className="mb-16">
                                       <SectionCard
                                           title={t('workflow.title')}
                                           subtitle={t('workflow.subtitle')}
                                           icon={<ClipboardDocumentListIcon className="h-5 w-5 text-slate-700" />}
                                       >
                                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                                                     {[
                                                            { step: 1, title: t('workflow.step1.title'), desc: t('workflow.step1.description') },
                                                            { step: 2, title: t('workflow.step2.title'), desc: t('workflow.step2.description') },
                                                            { step: 3, title: t('workflow.step3.title'), desc: t('workflow.step3.description') }
                                                     ].map((item) => (
                                                         <div key={item.step} className="flex gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-5">
                                                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-sm font-extrabold text-slate-700 shadow-sm">
                                                                       {item.step}
                                                                </div>
                                                                <div className="text-sm">
                                                                       <div className="font-bold text-slate-900 mb-1">{item.title}</div>
                                                                       <div className="text-slate-600 leading-relaxed">{item.desc}</div>
                                                                </div>
                                                         </div>
                                                     ))}
                                              </div>
                                       </SectionCard>
                                </div>

                                {/* FAQ and Support */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
                                       <SectionCard
                                           title={t('faq.title')}
                                           subtitle={t('faq.subtitle')}
                                           icon={<QuestionMarkCircleIcon className="h-5 w-5 text-slate-700" />}
                                       >
                                              <div className="grid gap-4">
                                                     <FaqItem q={t('faq.migration.q')} a={t('faq.migration.a')} />
                                                     <FaqItem q={t('faq.pricing.q')} a={t('faq.pricing.a')} />
                                                     <FaqItem q={t('faq.start.q')} a={t('faq.start.a')} />
                                              </div>
                                       </SectionCard>

                                       <div className="flex flex-col justify-center rounded-3xl border border-slate-200 bg-gradient-to-br from-indigo-600 to-blue-700 p-8 text-white shadow-xl">
                                              <h3 className="text-2xl font-extrabold mb-4">{t('cta.title')}</h3>
                                              <p className="text-indigo-100 mb-8 leading-relaxed">
                                                     {t('cta.subtitle')}
                                              </p>
                                              <div className="flex flex-col sm:flex-row gap-4">
                                                     <Button gray onClick={() => router.push('/signup')} className="grow py-4 bg-white text-indigo-900 border-none font-extrabold hover:bg-slate-50">
                                                            {t('cta.button')}
                                                     </Button>
                                                     <Button blue onClick={() => router.push('/how-it-works')} className="grow py-4 bg-indigo-500 text-white border-none font-extrabold hover:bg-indigo-400">
                                                            {t('cta.learnMore')}
                                                     </Button>
                                              </div>
                                       </div>
                                </div>

                                <SeoInternalLinks />
                         </div>
                  </div>
           </>
       );
};
